import { PropsWithChildren, ReactNode, useEffect, useState } from 'react'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { useStore } from 'hooks/useStore'
import clsx from 'clsx'
import { Button } from 'components/atoms/button'

export interface ConfirmationModalProps {
	open: boolean
	title?: ReactNode
	setOpen?: (...args: any) => any
	onOpened?: (...args: any) => any
	onClosed?: (...args: any) => any
	blur?: boolean
	loading?: boolean
	size?: string
	className?: string
	hideCloseButton?: boolean
	/** When true, the modal can't be dismissed by backdrop click or Escape (only via the buttons). */
	preventClose?: boolean
	onConfirm?: () => void
	onCancel?: () => void
	confirmLabel?: string
	cancelLabel?: string
	/** Stacking class for the dialog; raise it to open over a full-screen layer such as the ticket view. */
	zIndexClassName?: string
}

export function ConfirmationModal({
	children,
	open,
	setOpen,
	title = '',
	onOpened,
	onClosed,
	blur,
	loading,
	size = '',
	className = '',
	hideCloseButton = true,
	preventClose = false,
	onConfirm,
	onCancel,
	confirmLabel,
	cancelLabel,
	zIndexClassName,
}: PropsWithChildren<ConfirmationModalProps>) {
	const { setBlur } = useStore()

	useEffect(() => {
		return () => {
			onClosed?.(false)
			setBlur(false)
		}
	}, [])

	useEffect(() => {
		if (open) {
			onOpened?.(open)
		} else {
			onClosed?.(open)
		}
		setBlur(blur && open)
	}, [open, blur, onOpened, onClosed, setBlur])

	const modalSize = size === 'lg' ? 'xl' : size === 'md' ? 'lg' : size === 'sm' ? 'sm' : 'sm'

	const handleCancel = () => {
		onCancel?.()
		setOpen?.(false)
	}

	return (
		<BaseModal
			open={open}
			setOpen={loading ? undefined : setOpen}
			title={title}
			hideCloseButton={hideCloseButton}
			className={clsx('px-6', className)}
			size={modalSize}
			preventClose={loading || preventClose}
			onClose={handleCancel}
			onOpened={onOpened}
			onClosed={onClosed}
			zIndexClassName={zIndexClassName}
		>
			{children}
			{(onConfirm || onCancel) && (
				<div className="mt-6 space-y-2">
					{onConfirm && (
						<Button variant="primary" fullWidth onClick={onConfirm} disabled={loading}>
							{loading && <span className="mr-2">⏳</span>}
							{confirmLabel || 'Confirm'}
						</Button>
					)}
					{onCancel && (
						<Button variant="secondary" fullWidth onClick={handleCancel} disabled={loading}>
							{cancelLabel || 'Cancel'}
						</Button>
					)}
				</div>
			)}
		</BaseModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface ConfirmationModalManagedProps {
	title?: string
	message: string
	confirmLabel?: string
	cancelLabel?: string
	zIndexClassName?: string
	onConfirm: () => void
	onCancel?: () => void
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		confirmation: ConfirmationModalManagedProps
	}
}

export function ManagedConfirmationModal({
	onClose,
	onConfirm,
	onCancel,
	message,
	...rest
}: ConfirmationModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		// Allow Headless UI leave transition (~150 ms) to finish before unmounting
		setTimeout(onClose, 200)
	}

	return (
		<ConfirmationModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onConfirm={() => { onConfirm(); handleClose() }}
			onCancel={() => { onCancel?.(); handleClose() }}
			{...rest}
		>
			{message}
		</ConfirmationModal>
	)
}
